import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import TitleIcon from "@mui/icons-material/Title";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";
import SortableList, { SortableItem } from "react-easy-sort";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import colors from "assets/theme/base/colors";
import parse from "html-react-parser";
import { convert } from "html-to-text";

const PostReorderModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      post,
      setPost,
      handleRefresh = () => {},
      handleAfterOk = () => {},
    },
    ref
  ) => {
    const [contentList, setContentList] = useState(post?.metadata?.content);

    if (ref) {
      useImperativeHandle(
        ref,
        () => ({
          contentList,
        }),
        [setContentList]
      );
    }

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    const handleSortEnd = (oldIndex, newIndex) => {
      console.log("resort:", oldIndex, newIndex);
      if (contentList.length > 0) {
        var updatedList = [...contentList];
        // Remove dragged item
        const [reorderedItem] = updatedList.splice(oldIndex, 1);
        updatedList.splice(newIndex, 0, reorderedItem);

        setContentList(updatedList);
      }
    };

    const handleSaveNewOrder = () => {
      setPost({
        ...post,
        metadata: {
          ...post?.metadata,
          content: contentList,
        },
      });
      handleClose();
    };

    return (
      <Dialog
        open={visible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
        fullWidth={true}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {"Reorder Content"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <div style={{ overflow: "auto", maxHeight: "600px" }}>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <SuiBox sx={{ px: 2 }}>
                <SortableList onSortEnd={handleSortEnd} distance={10}>
                  {contentList.map((item, index) => {
                    let text = item.type;
                    if (item?.caption) {
                      text = convert(item.caption, { wordwrap: 80 });
                    }

                    return (
                      <SortableItem key={index}>
                        <Card
                          sx={{
                            border: `solid 1px #e3e3e3`,
                            borderTop: index != 0 ? "none" : "",
                            borderRadius: 0,
                            cursor: "move",
                            backgroundColor: "#f5f5f5",
                            zIndex: 1300,
                          }}
                        >
                          <SuiBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <SuiBox
                              sx={{
                                width: 80,
                                height: 80,
                                minWidth: 80,
                                overflow: "hidden",
                                backgroundColor: "#efefef",
                                p: "1px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {item.img ? (
                                <ImageCardWrapper
                                  width={80}
                                  height={80}
                                  data={item.img}
                                  draggable={false}
                                />
                              ) : item?.type == "text" ? (
                                <TitleIcon fontSize="large" />
                              ) : (
                                <ViewQuiltIcon fontSize="large" />
                              )}
                            </SuiBox>

                            <SuiTypography variant="body1" color="text">
                              {text.slice(0, 30)}
                            </SuiTypography>
                          </SuiBox>
                        </Card>
                      </SortableItem>
                    );
                  })}
                </SortableList>
              </SuiBox>
              <SuiBox
                pt={3}
                px={3}
                display={"flex"}
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="text" size="medium" onClick={handleClose} color="dark">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton
                      size="medium"
                      variant="gradient"
                      color="dark"
                      onClick={handleSaveNewOrder}
                    >
                      Save
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </div>
      </Dialog>
    );
  }
);

export default PostReorderModal;
